import type { DynamicClient } from '@dynamic-labs-sdk/client';
import {
  emitEvent,
  filterDuplicates,
  formatWalletProviderGroupKey,
  formatWalletProviderKey,
  getActiveNetworkIdFromLastKnownRegistry,
  getWalletProviderRegistry,
  switchActiveNetworkInLastKnownRegistry,
} from '@dynamic-labs-sdk/client/core';
import { SessionClosedUnexpectedlyError } from '@dynamic-labs-sdk/wallet-connect';
import {
  createWalletConnectSessionEventEmitter,
  getSignClient,
  splitCaip10Token,
} from '@dynamic-labs-sdk/wallet-connect/core';
import { WalletProviderEnum } from '@dynamic-labs/sdk-api-core';
import { SDK_ERRORS } from '@walletconnect/utils';
import { getAddress as viemGetAddress } from 'viem';

import { evmConfirmTransaction } from '../../utils/evmConfirmTransaction';
import { evmExecuteSwapTransaction } from '../../utils/evmExecuteSwapTransaction';
import { evmTransferAmount } from '../../utils/evmTransferAmount';
import type { evmMethods } from '../consts';
import type { WalletConnectEvmWalletProvider } from '../walletConnectEvmWalletProvider.types';

type CreateWalletProviderFromEIP155SessionParams = {
  sessionTopic: string;
};

/**
 * The list of ethereum request methods that require user action.
 * We will raise a user action requested event for these methods.
 */
const methodsThatRequireUserAction: string[] = [
  'personal_sign',
  'eth_sendTransaction',
  // eslint-disable-next-line custom-rules/ban-ethereum-eth-terms
  'eth_signTypedData_v4',
] satisfies (typeof evmMethods)[number][];

/** @not-instrumented */
export const createWalletProviderFromEIP155Session = async (
  { sessionTopic }: CreateWalletProviderFromEIP155SessionParams,
  client: DynamicClient
): Promise<WalletConnectEvmWalletProvider> => {
  const signClient = await getSignClient(client);

  const getSession = () => {
    const session = signClient.session.get(sessionTopic);

    if (!session) {
      void disconnect();

      throw new SessionClosedUnexpectedlyError({
        sessionTopic,
        walletDisplayName: displayName,
      });
    }

    return session;
  };

  const displayName = getSession().peer.metadata.name;

  const namespaceKey = 'eip155';

  const walletProviderType = WalletProviderEnum.WalletConnect;

  const key = formatWalletProviderKey({
    chain: 'EVM',
    displayName,
    suffix: sessionTopic,
    walletProviderType,
  });

  const getActiveNetworkId: WalletConnectEvmWalletProvider['getActiveNetworkId'] =
    async () => {
      // Assert the session is still valid
      getSession();

      return await getActiveNetworkIdFromLastKnownRegistry({
        client,
        walletProviderKey: key,
      });
    };

  const switchActiveNetwork: WalletConnectEvmWalletProvider['switchActiveNetwork'] =
    async ({ networkId }) => {
      // Assert the session is still valid
      getSession();

      await switchActiveNetworkInLastKnownRegistry({
        client,
        networkId,
        walletProviderKey: key,
      });
    };

  const request: WalletConnectEvmWalletProvider['request'] = async (args) => {
    const { networkId } = await getActiveNetworkId();

    const session = getSession();

    if (methodsThatRequireUserAction.includes(args.method)) {
      emitEvent(
        {
          args: {
            walletMetadata: session.peer.metadata,
            walletProviderKey: key,
          },
          event: 'walletConnectUserActionRequested',
        },
        client
      );
    }

    return signClient.request({
      // signClient expects the chainId to be in CAIP-2 format "eip155:<networkId>"
      chainId: `${namespaceKey}:${networkId}`,
      request: args,
      topic: sessionTopic,
    });
  };

  const disconnect: NonNullable<
    WalletConnectEvmWalletProvider['disconnect']
  > = async () => {
    await signClient.disconnect({
      reason: SDK_ERRORS.USER_DISCONNECTED,
      topic: sessionTopic,
    });

    const walletProviderRegistry = getWalletProviderRegistry(client);

    walletProviderRegistry.unregister(key);
  };

  /**
   * We actually clear up the entire WC session and its wallet provider
   */
  const disconnectWalletAccount: WalletConnectEvmWalletProvider['disconnectWalletAccount'] =
    async () => disconnect();

  const signMessage: WalletConnectEvmWalletProvider['signMessage'] = async ({
    message,
    walletAccount,
  }) => {
    const params = [message];

    if (walletAccount) {
      params.push(walletAccount.address);
    }

    const requestPromise = request<string>({
      method: 'personal_sign',
      params,
    });

    const signature = await requestPromise;

    return { signature };
  };

  const getConnectedAddresses: WalletConnectEvmWalletProvider['getConnectedAddresses'] =
    async () => {
      const addresses = filterDuplicates(
        getSession().namespaces[namespaceKey].accounts.map(
          (account) => splitCaip10Token(account).address
        )
      ).map((address) => viemGetAddress(address));

      return { addresses };
    };

  const formatWalletAddress: WalletConnectEvmWalletProvider['formatWalletAddress'] =
    ({ address }) => viemGetAddress(address);

  const { cleanupEventEmitter, getEventEmitter } =
    createWalletConnectSessionEventEmitter({
      formatAddress: viemGetAddress,
      getSession,
      namespaceKey,
      sessionTopic,
      signClient,
    });

  const terminate: WalletConnectEvmWalletProvider['terminate'] = async () =>
    cleanupEventEmitter();

  return {
    chain: 'EVM',
    confirmTransaction: (args) => evmConfirmTransaction(args, client),
    disconnect,
    disconnectWalletAccount,
    get events() {
      return getEventEmitter();
    },
    executeSwapTransaction: (args) => evmExecuteSwapTransaction(args, client),
    formatWalletAddress,
    getActiveNetworkId,
    getConnectedAddresses,
    getSession,
    groupKey: formatWalletProviderGroupKey(displayName),
    key,
    metadata: {
      displayName,
      icon: getSession().peer.metadata.icons[0],
    },
    namespaceKey,
    request,
    signMessage,
    switchActiveNetwork,
    terminate,
    transferAmount: (args) => evmTransferAmount(args, client),
    walletProviderType,
  };
};
